import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Search, Trash2, Eye, CheckCircle, XCircle, X, ShieldCheck, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { toast } from 'sonner';

export default function GestionUsuarios() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [viewModal, setViewModal] = useState({ open: false, user: null });
  const [verifyModal, setVerifyModal] = useState({ open: false, user: null });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchUsers();
    }
  }, [filterType, filterVerified]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/users/all?page=${currentPage}&limit=${itemsPerPage}`;
      
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (filterType) url += `&user_type=${filterType}`;
      if (filterVerified) url += `&verified=${filterVerified === 'verified' ? 'true' : 'false'}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar usuarios');

      const data = await response.json();
      
      // Handle both old format (array) and new format (object)
      if (Array.isArray(data)) {
        setUsers(data);
        setTotalItems(data.length);
        setTotalPages(1);
      } else {
        setUsers(data.users || []);
        setTotalItems(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers();
  };

  const handleVerify = async (userId, verified) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ verified })
      });

      if (!response.ok) throw new Error('Error al actualizar');

      toast.success(verified ? 'Usuario verificado manualmente' : 'Verificacion removida');
      setVerifyModal({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.user) return;
    
    try {
      const response = await fetch(`${API_URL}/api/users/${deleteModal.user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Usuario eliminado');
      setDeleteModal({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getUserTypeBadge = (type) => {
    const types = {
      natural: { label: 'Persona Natural', color: 'bg-blue-100 text-blue-800' },
      juridica: { label: 'Persona Juridica', color: 'bg-purple-100 text-purple-800' },
      funcionario: { label: 'Funcionario', color: 'bg-green-100 text-green-800' },
      superadmin: { label: 'Super Admin', color: 'bg-red-100 text-red-800' }
    };
    return types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  // Pagination helpers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">Gestion de Usuarios</h1>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email, documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los tipos</option>
              <option value="natural">Persona Natural</option>
              <option value="juridica">Persona Juridica</option>
              <option value="funcionario">Funcionario</option>
            </select>
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="verified">Verificados</option>
              <option value="unverified">Sin verificar</option>
            </select>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Search className="h-4 w-4" /> Buscar
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm text-slate-600">
              Mostrando <span className="font-semibold">{users.length}</span> de <span className="font-semibold">{totalItems}</span> usuarios
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Por pagina:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
              <RefreshCw className="h-5 w-5 animate-spin" /> Cargando...
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No se encontraron usuarios</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0F172A] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tipo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Documento</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nombre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Verificado</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {users.map((user) => {
                    const typeBadge = getUserTypeBadge(user.user_type);
                    return (
                      <tr key={user.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${typeBadge.color}`}>
                            {typeBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 font-mono">
                          {user.document_id || user.nit || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                          {user.user_type === 'juridica' 
                            ? user.company_name 
                            : `${user.first_name || ''} ${user.first_lastname || ''}`.trim() || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{user.email}</td>
                        <td className="px-4 py-3 text-center">
                          {user.verified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3" />
                              Verificado
                            </span>
                          ) : (
                            <button
                              onClick={() => setVerifyModal({ open: true, user })}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                              title="Verificar manualmente"
                            >
                              <ShieldCheck className="h-4 w-4" />
                              Verificar
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => setViewModal({ open: true, user })} className="p-1 text-slate-500 hover:text-blue-600" title="Ver detalles">
                              <Eye className="h-5 w-5" />
                            </button>
                            <button onClick={() => setDeleteModal({ open: true, user })} className="p-1 text-slate-500 hover:text-red-600" title="Eliminar">
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="text-sm text-slate-600">
                Pagina {currentPage} de {totalPages}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Primera pagina"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Pagina anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Pagina siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Ultima pagina"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* View Modal */}
      {viewModal.open && viewModal.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Detalles del Usuario</h3>
              <button onClick={() => setViewModal({ open: false, user: null })} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Tipo de Usuario</p>
                  <p className="font-medium">{getUserTypeBadge(viewModal.user.user_type).label}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium">{viewModal.user.email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Documento</p>
                  <p className="font-medium">{viewModal.user.document_id || viewModal.user.nit || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Telefono</p>
                  <p className="font-medium">{viewModal.user.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Verificado</p>
                  <p className="font-medium">{viewModal.user.verified ? 'Si' : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Fecha Registro</p>
                  <p className="font-medium">{viewModal.user.created_at ? new Date(viewModal.user.created_at).toLocaleDateString('es-CO') : '-'}</p>
                </div>
              </div>
              
              {/* Verification Actions */}
              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 mb-2">Acciones de Verificacion</p>
                {viewModal.user.verified ? (
                  <button
                    onClick={() => {
                      handleVerify(viewModal.user.id, false);
                      setViewModal({ open: false, user: null });
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                  >
                    <XCircle className="h-4 w-4" />
                    Remover Verificacion
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setViewModal({ open: false, user: null });
                      setVerifyModal({ open: true, user: viewModal.user });
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Verificar Manualmente
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verify Modal */}
      {verifyModal.open && verifyModal.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-full">
                <ShieldCheck className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Verificacion Manual</h3>
            </div>
            <p className="text-slate-600 mb-2">
              Esta seguro que desea verificar manualmente al usuario?
            </p>
            <div className="bg-slate-50 rounded-lg p-3 mb-6">
              <p className="text-sm font-medium text-slate-900">{verifyModal.user.email}</p>
              <p className="text-xs text-slate-500">
                {verifyModal.user.user_type === 'juridica' 
                  ? verifyModal.user.company_name 
                  : `${verifyModal.user.first_name || ''} ${verifyModal.user.first_lastname || ''}`.trim()}
              </p>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg mb-6">
              <strong>Nota:</strong> Esta accion permite al usuario acceder al sistema sin haber completado la verificacion por correo electronico.
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setVerifyModal({ open: false, user: null })} 
                className="px-4 py-2 text-slate-700 border rounded-lg hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button 
                onClick={() => handleVerify(verifyModal.user.id, true)} 
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                Verificar Usuario
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Confirmar eliminacion</h3>
            <p className="text-slate-600 mb-6">
              Esta seguro que desea eliminar al usuario <strong>"{deleteModal.user?.email}"</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteModal({ open: false, user: null })} className="px-4 py-2 text-slate-700 border rounded-lg hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
